/**
 * Country-specific public market locale configuration shared by the API and
 * web client. This is URL/language/currency metadata only; it does not enable
 * payment, booking, or rollout capabilities.
 */
export interface MarketLocale {
  countryCode: string;
  market: string;
  defaultLanguage: string;
  languages: readonly string[];
  currency: string;
}

export const MARKET_LOCALE_REGISTRY: Readonly<Record<string, MarketLocale>> = {
  ar: { countryCode: "AR", market: "ar", defaultLanguage: "es", languages: ["es", "en"], currency: "ARS" },
  at: { countryCode: "AT", market: "at", defaultLanguage: "de", languages: ["de", "en"], currency: "EUR" },
  be: { countryCode: "BE", market: "be", defaultLanguage: "nl", languages: ["nl", "fr", "en"], currency: "EUR" },
  bg: { countryCode: "BG", market: "bg", defaultLanguage: "bg", languages: ["bg", "en"], currency: "EUR" },
  br: { countryCode: "BR", market: "br", defaultLanguage: "pt-BR", languages: ["pt-BR", "en"], currency: "BRL" },
  ch: { countryCode: "CH", market: "ch", defaultLanguage: "de", languages: ["de", "fr", "it", "en"], currency: "CHF" },
  co: { countryCode: "CO", market: "co", defaultLanguage: "es", languages: ["es", "en"], currency: "COP" },
  cz: { countryCode: "CZ", market: "cz", defaultLanguage: "cs", languages: ["cs", "en"], currency: "CZK" },
  ec: { countryCode: "EC", market: "ec", defaultLanguage: "es", languages: ["es", "en"], currency: "USD" },
  fi: { countryCode: "FI", market: "fi", defaultLanguage: "fi", languages: ["fi", "en"], currency: "EUR" },
  in: { countryCode: "IN", market: "in", defaultLanguage: "en-IN", languages: ["en-IN"], currency: "INR" },
  lt: { countryCode: "LT", market: "lt", defaultLanguage: "en", languages: ["en"], currency: "EUR" },
  lv: { countryCode: "LV", market: "lv", defaultLanguage: "en", languages: ["en"], currency: "EUR" },
  mx: { countryCode: "MX", market: "mx", defaultLanguage: "es", languages: ["es", "en"], currency: "MXN" },
  nl: { countryCode: "NL", market: "nl", defaultLanguage: "nl", languages: ["nl", "en"], currency: "EUR" },
  no: { countryCode: "NO", market: "no", defaultLanguage: "no", languages: ["no", "en"], currency: "NOK" },
  pl: { countryCode: "PL", market: "pl", defaultLanguage: "pl", languages: ["pl", "en"], currency: "PLN" },
  sk: { countryCode: "SK", market: "sk", defaultLanguage: "sk", languages: ["sk", "en"], currency: "EUR" },
};

const MARKET_BY_COUNTRY: Readonly<Record<string, MarketLocale>> = Object.freeze(
  Object.fromEntries(Object.values(MARKET_LOCALE_REGISTRY).map((entry) => [entry.countryCode, entry])),
);

export function countryCodeToMarketLocale(countryCode?: string | null): MarketLocale | undefined {
  if (!countryCode) return undefined;
  return MARKET_BY_COUNTRY[countryCode.trim().toUpperCase()];
}

export function marketLocaleForPath(market?: string | null): MarketLocale | undefined {
  if (!market) return undefined;
  return MARKET_LOCALE_REGISTRY[market.trim().toLowerCase()];
}